/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { Divider, Button, Icon, Box, Heading, FlexCol, FlexRow } from 'sarsaparilla';
import {
    dateSelectionDefault,
    dateSelectionProps,
} from 'ui-permit/src/props/multi-division/DateSelection';
import { LOTTERY_CHOICES_PROP_PATH } from 'ui-permit/shared-components/constants/propPaths';
import HowTheLotteryWorksModal from 'ui-permit/shared-components/registration/LotteryPreferences/HowTheLotteryWorksModal';
import { databindings } from '../../../constants/registration';
import { HuntingLotteryApplicationProps, LotteryProps } from '../../../props';
import PreferredDivisionGroupSizeItem from './PreferredDivisionGroupSizeItem';

const propTypes = {
    permit: PropTypes.shape({}),
    lottery: LotteryProps.isRequired,
    reservation: HuntingLotteryApplicationProps.isRequired,

    divisions: PropTypes.shape({}),
    selections: PropTypes.arrayOf(dateSelectionProps),
    maxGroupSize: PropTypes.number,
    minGroupSize: PropTypes.number,
    maxChoices: PropTypes.number,
    hideGroupSize: PropTypes.bool,

    onAdd: PropTypes.func,
    onChange: PropTypes.func,
    onRemove: PropTypes.func,

    pActions: PropTypes.shape({
        updatePendingReservation: PropTypes.func.isRequired,
        formValidationChange: PropTypes.func.isRequired,
    }).isRequired,

    preferredDivisionLabel: PropTypes.string,
};

const defaultProps = {
    allowMore: true,
};

export default function PreferredDivisionGroupSizeList(props) {
    const [modalIsOpen, setModalIsOpen] = useState(false);
    const lotteryChoicesPropPath = databindings[LOTTERY_CHOICES_PROP_PATH];

    const onFormValidate = useCallback(
        (path) => {
            return (isInvalid, error) =>
                props.pActions.formValidationChange(
                    `${props.reservation.id}_${path}`,
                    isInvalid ? error : null
                );
        },
        [props.reservation.id]
    );

    const onAddPreference = () => {
        const newPref = { ...dateSelectionDefault };
        if (props.onAdd) {
            props.onAdd(newPref);
            return;
        }

        props.selections.push(newPref);
        props.pActions.updatePendingReservation(
            props.reservation.id,
            lotteryChoicesPropPath,
            props.selections
        );
    };

    const onChangePreference = (idx, newValue) => {
        if (props.onChange) {
            props.onChange(idx, newValue);
            return;
        }
        const selections = props.selections;
        selections[idx] = newValue;
        props.pActions.updatePendingReservation(
            props.reservation.id,
            lotteryChoicesPropPath,
            selections
        );
    };

    const onRemovePreference = (idxToRemove) => {
        if (props.selections.length > 0) {
            if (props.onRemove) {
                props.onRemove(idxToRemove, props.selections[idxToRemove]);
                return;
            }

            props.selections.splice(idxToRemove, 1);
            props.pActions.updatePendingReservation(
                props.reservation.id,
                lotteryChoicesPropPath,
                props.selections
            );
        }
    };

    const minGroupSize = props.minGroupSize > 1 ? props.minGroupSize : 1;
    const maxGroupSize = props.maxGroupSize > 1 ? props.maxGroupSize : 1;

    return (
        <>
            <FlexRow hasGutters={false} justifyContent="space-between">
                <FlexCol xs="variable">
                    <Heading headingLevel={4} appearance={'h5'}>
                        Lottery Preferences
                    </Heading>
                </FlexCol>
                <FlexCol xs="variable">
                    <Button
                        appearance="link"
                        iconBeforeElement={<Icon iconName="help" />}
                        onClick={() => setModalIsOpen(true)}
                    >
                        How do the choices work?
                    </Button>
                </FlexCol>
            </FlexRow>
            <p id="preferred-dates-text">
                Choose your preferred dates below. You can apply up to{' '}
                <b>{props.maxChoices} choices</b>. The more dates you add the better your
                chances at winning the lottery!
            </p>

            <HowTheLotteryWorksModal
                isOpen={modalIsOpen}
                isDateRange={false}
                closeModal={() => setModalIsOpen(false)}
            />

            {props.selections.length > 0 && (
                <Box padding="xs" background="gray-2">
                    <div className="per-preferred-dates-list">
                        {props.selections.map((selection, idx) => {
                            return (
                                <>
                                    <PreferredDivisionGroupSizeItem
                                        divisions={props.divisions}
                                        selection={selection}
                                        onChange={(value) =>
                                            onChangePreference(idx, value)
                                        }
                                        onRemove={onRemovePreference}
                                        index={idx}
                                        minGroupSize={minGroupSize}
                                        maxGroupSize={maxGroupSize}
                                        onFormValidate={onFormValidate}
                                        hideGroupSize={props.hideGroupSize}
                                        preferredDivisionLabel={
                                            props.preferredDivisionLabel
                                        }
                                    />
                                    {idx < props.selections.length - 1 ? (
                                        <Divider />
                                    ) : null}
                                </>
                            );
                        })}
                    </div>
                </Box>
            )}
            <div className="rec-form-item-wrap">
                <Button
                    appearance="link"
                    iconBeforeElement={<Icon iconName="add-circle" />}
                    onClick={onAddPreference}
                    isDisabled={props.selections.length >= props.maxChoices}
                    className="addAnotherChoiceLink"
                >
                    Add another choice
                </Button>
            </div>
        </>
    );
}

PreferredDivisionGroupSizeList.propTypes = propTypes;
PreferredDivisionGroupSizeList.defaultProps = defaultProps;
